import React, { useState, useEffect } from 'react';

import { LeftPositions, RightPositions } from './positions'

import styles from './OvarianViewer.module.css'
import { BreedingStructureKind, Side } from '../../config/constant';

export type Structures = {
  [key in Side]: {
    [key in BreedingStructureKind]: number
  }
}
export const initStructures : () => Structures = () => ({
  'R' : {'CL1': 0, 'CL2': 0, 'CL3': 0, 'CCL': 0, 'CLCy': 0, 'F1': 0, 'F2': 0, 'F3': 0, 'CyF': 0, 'FCy': 0, 'OV': 0, 'OTHER': 0,},
  'L'  : {'CL1': 0, 'CL2': 0, 'CL3': 0, 'CCL': 0, 'CLCy': 0, 'F1': 0, 'F2': 0, 'F3': 0, 'CyF': 0, 'FCy': 0, 'OV': 0, 'OTHER': 0,},
})

const W = 140
const H = 200
const SIZE: Map<BreedingStructureKind, number> = new Map([
  ['CL1'   , 10],
  ['CL2'   , 20],
  ['CL3'   , 35],
  ['CCL'   , 35],
  ['CLCy'  , 50],
  ['F1'    , 5 ],
  ['F2'    , 10],
  ['F3'    , 20],
  ['CyF'   , 30],
  ['FCy'   , 50],
  ['OV'    , 0 ],
  ['OTHER' , 20],
])
const SortedKind = Array.from(SIZE.entries()).sort((a, b) => b[1] - a[1]).map(k => k[0])

type Position = {
  kind: BreedingStructureKind,
  x : number,
  y : number,
  rotate?: number
}

const getRandomPosition = (size: number) => {
  const rad = Math.random() * 2 * Math.PI
  const r   = Math.random() * ( 1 - size / (H/2) ) //todo

  const x = (W/2 * Math.cos(rad) * r) + W/2
  const y = (H/2 * Math.sin(rad) * r) + H/2

  return { x, y }
}

const getSparsePosition = (size: number, others: Array<Position>) => {
  let n = 10

  let bestPos = getRandomPosition(size)
  let bestDist = 0

  while(n) {
    const pos = getRandomPosition(size)
    let distance = Math.min(...others.map(p => Math.sqrt((p.x - pos.x)**2 + (p.y - pos.y)**2)))
    if (distance > bestDist) {
      bestPos = pos
      bestDist = distance
    }

    // if (bestDist < ) {
    // }
    n--
  }

  return bestPos
}

const getFixedPosition = (side: Side, kind: BreedingStructureKind, index: number) => side === 'L' ? LeftPositions[kind][index] : RightPositions[kind][index]


const OneSideOvarian: React.FC<{
  structures : Structures,
  side : Side,
}> = ({ structures, side }) => {
  const [positions, setPositions] = useState<Array<Position>>([])

  useEffect(() => {
    let pos: Array<Position> = []

    SortedKind.forEach(kind => {
      const count = structures[side][kind]
      for(let i=0; i < count; i++) {
        // const { x, y } = getSparsePosition(size, pos)
        const { x, y, rotate } = getFixedPosition(side, kind, i)
        pos.push({ kind, x, y, rotate })
      }
    })

    setPositions(pos)
  }, [structures[side]])

  return (
    <svg viewBox={ `-15 -10 ${ W + 30 } ${ H + 20 }` } xmlns="http://www.w3.org/2000/svg" version="1.1">
      <ellipse cx={ W/2 } cy={ H/2 } rx={ W/2 } ry={ H/2 } style={{ fill: "#ead6d3" }} />
      {positions.map((position,i) => {
        return ({
          'CL1'    : <ObjectCL1   key={i} position={ position } />,
          'CL2'    : <ObjectCL2   key={i} position={ position } />,
          'CL3'    : <ObjectCL3   key={i} position={ position } />,
          'CCL'    : <ObjectCCL   key={i} position={ position } />,
          'CLCy'   : <ObjectCLCY  key={i} position={ position } />,
          'F1'     : <ObjectF1    key={i} position={ position } />,
          'F2'     : <ObjectF2    key={i} position={ position } />,
          'F3'     : <ObjectF3    key={i} position={ position } />,
          'CyF'    : <ObjectCYF   key={i} position={ position } />,
          'FCy'    : <ObjectFCY   key={i} position={ position } />,
          'OV'     : <ObjectOV    key={i} position={ position } />,
          'OTHER'  : <ObjectOther key={i} position={ position } />,
        }[position.kind])
      })}
    </svg>
  )
}



const ObjectCL1: React.FC<{ position : Position }> = ({ position }) =>
  <g fill="yellow" stroke="gray" transform={ `translate(${ position.x - (SIZE.get('CL1') ?? 1) },${ position.y - (SIZE.get('CL1') ?? 1) })` }>
    <path
      strokeWidth={ 1 / ( (SIZE.get('CL1') ?? 1) * 2 / 100 ) }
      transform={ `scale(${ (SIZE.get('CL1') ?? 1) * 2 / 100 })` }
      d="M51.996 99.965c3.664 0 7.253-2.407 9.81-5.312a12.178 12.172 0 004.044.694c6.632 0 12.057-4.57 13.275-11.615a9.2 9.2 0 003.794.82c4.2 0 11.23-3.504 10.857-13.075 4.136-2.32 6.215-6.681 6.215-12.38 0-3.167-1.389-7.383-3.517-9.112 2.128-1.733 3.357-5.442 3.517-8.608.341-6.72-.69-12.96-7.573-13.852 1.556-4.436-5.3-12.107-9.5-12.107-1.316 0-2.534.352-3.729.89-1.218-7.044-6.82-12.455-13.452-12.455-1.378 0-2.895 1.148-4.211 1.607C58.976 2.556 55.664 0 51.999 0a12.557 12.551 0 00-7.728 2.7A12.497 12.49 0 0036.532 0c-4.958 0-9.5 3.034-11.897 7.834a13.825 13.818 0 00-1.848-.125c-6.688 0-11.43 8.467-11.76 15.96-4.913 2.575-9.33 5.767-10.471 11.793C-.69 42.06.499 48.18 5.545 51.008.499 53.83.673 60.188.673 65.274c0 6.15 4.466 10.408 9.181 13.495.33 7.963 6.469 12.593 13.157 12.593.619 0 1.237-.038 1.848-.122 2.397 4.8 6.715 8.725 11.67 8.725 2.8 0 5.459-.932 7.739-2.7a12.557 12.551 0 007.724 2.7z"
    />
  </g>

const ObjectCL2: React.FC<{ position : Position }> = ({ position }) =>
  <g fill="yellow" stroke="gray" transform={ `translate(${ position.x - (SIZE.get('CL2') ?? 1) },${ position.y - (SIZE.get('CL2') ?? 1) })` }>
    <path
      strokeWidth={ 1 / ( (SIZE.get('CL2') ?? 1) * 2 / 100 ) }
      transform={ `scale(${ (SIZE.get('CL2') ?? 1) * 2 / 100 })` }
      d="M51.996 99.965c3.664 0 7.253-2.407 9.81-5.312a12.178 12.172 0 004.044.694c6.632 0 12.057-4.57 13.275-11.615a9.2 9.2 0 003.794.82c4.2 0 11.23-3.504 10.857-13.075 4.136-2.32 6.215-6.681 6.215-12.38 0-3.167-1.389-7.383-3.517-9.112 2.128-1.733 3.357-5.442 3.517-8.608.341-6.72-.69-12.96-7.573-13.852 1.556-4.436-5.3-12.107-9.5-12.107-1.316 0-2.534.352-3.729.89-1.218-7.044-6.82-12.455-13.452-12.455-1.378 0-2.895 1.148-4.211 1.607C58.976 2.556 55.664 0 51.999 0a12.557 12.551 0 00-7.728 2.7A12.497 12.49 0 0036.532 0c-4.958 0-9.5 3.034-11.897 7.834a13.825 13.818 0 00-1.848-.125c-6.688 0-11.43 8.467-11.76 15.96-4.913 2.575-9.33 5.767-10.471 11.793C-.69 42.06.499 48.18 5.545 51.008.499 53.83.673 60.188.673 65.274c0 6.15 4.466 10.408 9.181 13.495.33 7.963 6.469 12.593 13.157 12.593.619 0 1.237-.038 1.848-.122 2.397 4.8 6.715 8.725 11.67 8.725 2.8 0 5.459-.932 7.739-2.7a12.557 12.551 0 007.724 2.7z"
    />
  </g>

const ObjectCL3: React.FC<{ position : Position }> = ({ position }) =>
  <g fill="yellow" stroke="gray" transform={ `translate(${ position.x - (SIZE.get('CL3') ?? 1) },${ position.y - (SIZE.get('CL3') ?? 1) })` }>
    <path
      strokeWidth={ 1 / ( (SIZE.get('CL3') ?? 1) * 2 / 100 ) }
      transform={ `scale(${ (SIZE.get('CL3') ?? 1) * 2 / 100 })` }
      d="M51.996 99.965c3.664 0 7.253-2.407 9.81-5.312a12.178 12.172 0 004.044.694c6.632 0 12.057-4.57 13.275-11.615a9.2 9.2 0 003.794.82c4.2 0 11.23-3.504 10.857-13.075 4.136-2.32 6.215-6.681 6.215-12.38 0-3.167-1.389-7.383-3.517-9.112 2.128-1.733 3.357-5.442 3.517-8.608.341-6.72-.69-12.96-7.573-13.852 1.556-4.436-5.3-12.107-9.5-12.107-1.316 0-2.534.352-3.729.89-1.218-7.044-6.82-12.455-13.452-12.455-1.378 0-2.895 1.148-4.211 1.607C58.976 2.556 55.664 0 51.999 0a12.557 12.551 0 00-7.728 2.7A12.497 12.49 0 0036.532 0c-4.958 0-9.5 3.034-11.897 7.834a13.825 13.818 0 00-1.848-.125c-6.688 0-11.43 8.467-11.76 15.96-4.913 2.575-9.33 5.767-10.471 11.793C-.69 42.06.499 48.18 5.545 51.008.499 53.83.673 60.188.673 65.274c0 6.15 4.466 10.408 9.181 13.495.33 7.963 6.469 12.593 13.157 12.593.619 0 1.237-.038 1.848-.122 2.397 4.8 6.715 8.725 11.67 8.725 2.8 0 5.459-.932 7.739-2.7a12.557 12.551 0 007.724 2.7z"
    />
  </g>

const ObjectCCL: React.FC<{ position : Position }> = ({ position }) =>
  <g fill="yellow" stroke="gray" transform={ `translate(${ position.x - (SIZE.get('CCL') ?? 1) },${ position.y - (SIZE.get('CCL') ?? 1) })` }>
    <path
      strokeWidth={ 1 / ( (SIZE.get('CCL') ?? 1) * 2 / 100 ) }
      transform={ `scale(${ (SIZE.get('CCL') ?? 1) * 2 / 100 })` }
      d="M51.917 99.954c3.663 0 7.244-2.405 9.796-5.3a12.22 12.22 0 004.034.688c6.624 0 12.037-4.553 13.255-11.58 1.194.54 2.47.814 3.787.814 4.19 0 11.213-3.492 10.838-13.035 4.13-2.315 6.204-6.665 6.204-12.347 0-3.16-1.387-7.366-3.511-9.09 2.124-1.726 3.35-5.426 3.51-8.582.342-6.702-.687-12.927-7.56-13.819 1.555-4.42-5.29-12.073-9.48-12.073-1.318 0-2.532.349-3.726.888C77.85 9.492 72.255 4.097 65.636 4.097c-1.375 0-2.89 1.144-4.198 1.6C58.88 2.804 55.576.253 51.912.253c-2.86 0-5.516.995-7.71 2.696A12.547 12.641 0 0036.476.252c-4.948 0-9.484 3.028-11.876 7.814a13.922 14.026 0 00-1.844-.125c-6.676 0-11.41 8.444-11.74 15.918C6.118 26.43 1.71 29.61.57 35.62-.674 42.198.512 48.303 5.55 51.123.513 53.943.685 60.281.685 65.35c0 6.138 4.458 10.385 9.163 13.462.33 7.946 6.463 12.562 13.14 12.562.617 0 1.235-.041 1.844-.124 2.387 4.786 6.697 8.705 11.65 8.705 2.79 0 5.445-.933 7.721-2.696 2.194 1.7 4.85 2.696 7.71 2.696zm7.15-25.514c-3.87-.274-3.771-3.14-6.027-2.165-2.26.979-.564 2.961-5.433 3.168-6.08-2.554-3.112-6.698-9.567-5.847-4-2.364-9.237 1.688-9.53-1.348-.328-3.484-1.666-5.927 1.997-7.354 2.35-.916 3.491-3.844 2.944-5.034-2.355-5.13-1.573-1.892-2.919-7.395-.81-3.33 1.367-3.903 3.734-5.222 3.005.892 3.885-2.654 4.486-3.761 2.293-4.218 1.371-4.102 1.964-6.868 1.906-4.79 4.507-1.543 5.54-1.555 1.865 0 4.356 3.865 7.813 3.085 4.99-2.405 8.789 2.281 7.74 5.475-1.536 4.661.617 6.86 4.305 10.509 3.692 3.65-2.1 6.283-3.063 9.456-.947 3.127-3.075 3.488-2.684 9.44.214 3.865 2.565 5.694-1.3 5.416z"
    />
  </g>

const ObjectCLCY: React.FC<{ position : Position }> = ({ position }) =>
  <g fill="yellow" stroke="gray" transform={ `translate(${ position.x - (SIZE.get('CLCy') ?? 1) },${ position.y - (SIZE.get('CLCy') ?? 1) })` }>
    <path
      strokeWidth={ 1 / ( (SIZE.get('CLCy') ?? 1) * 2 / 100 ) }
      transform={ `scale(${ (SIZE.get('CLCy') ?? 1) * 2 / 100 })` }
      d="M52.013 100.09c3.66 0 7.243-2.412 9.79-5.318 1.312.46 2.665.693 4.04.693 6.617 0 12.032-4.572 13.246-11.624a9.22 9.22 0 003.787.818c4.194 0 11.21-3.507 10.838-13.087 4.13-2.32 6.202-6.687 6.202-12.396 0-3.168-1.387-7.39-3.512-9.12 2.125-1.735 3.354-5.447 3.512-8.615.34-6.732-.69-12.977-7.559-13.871 1.553-4.439-5.287-12.121-9.481-12.121-1.311 0-2.525.353-3.723.893C77.939 9.291 72.347 3.875 65.73 3.875c-1.375 0-2.89 1.148-4.202 1.609-2.55-2.906-5.855-5.466-9.515-5.466a12.586 12.703 0 00-7.71 2.7A12.526 12.643 0 0036.58.023c-4.94-.004-9.478 3.031-11.87 7.835a13.886 14.016 0 00-1.847-.122c-6.67 0-11.407 8.474-11.738 15.975-4.9 2.578-9.308 5.777-10.442 11.805C-.562 42.122.625 48.25 5.656 51.08.626 53.905.795 60.272.795 65.36c0 6.157 4.458 10.421 9.16 13.51.332 7.975 6.463 12.608 13.137 12.608.614 0 1.232-.038 1.846-.126 2.39 4.808 6.696 8.737 11.644 8.737 2.793 0 5.445-.932 7.722-2.7a12.586 12.703 0 007.71 2.7zm36.485-43.435c-2.645 3.61-4.296 2.757-5.509 5.047-1.424 2.693 1.168 8.523-1.545 9.242-3.086.814-6.237-1.282-6.444 1.898-.61 9.432-1.093 4.74-2.977 6.238-2.57 2.13-2.834 1.666-5.404 4.461-1.982 2.67-3.745-.133-6.632 1.43-5.151 2.788-4.816-1.582-9.662 2.761-2.362 3.146-4.83 1.077-9.7 1.286-4.095.175-2.969-3.712-4.634-7.934-.31-.776-1.055-1.21-1.79-1.038a11.003 11.106 0 01-2.435.274c-5.456 0-8.026.532-9.45-5.401a1.771 1.788 0 00-.803-1.13c-3.998-2.366-8.29-1.696-9.304-4.64-1.394-4.062-1.783-5.313 1.884-6.747 2.351-.92 1.353-7.828.245-8.341-3.889-1.81-6.282-4.207-5.05-11.593.566-3.408 1.365-3.918 3.731-5.237 3.007.89 4.673-2.796 5.276-3.906 2.287-4.234-2.45-4.496-1.858-7.273.17-3.833 2.317-5.476 5.641-7.245.739-.392 4.443-4.458 5.238-4.275.739.171 2.578-.533 2.578-.533 1.669-4.221.116-5.69 4.216-5.69 1.866 0 2.269.872 3.43 1.206 1.857.533 2.694 3.138 5.652 1.274 5.343-3.373 11.881 2.005 16.124 2.199 4.259-2.396 4.982 1.259 7.341 2.331 3.38 1.537 7.424 2.568 9.636 5.823 2.027 2.99 1.865 5.02 2.257 10.996 1.74 6.682 4.394 2.476 5.426 4.785.833 1.87 1.545-2.039.143 6.275-.24 3.248 2.804 3.248 3.531 5.298.905 2.541 3.497 4.545.848 8.155z"
    />
  </g>

const ObjectF1: React.FC<{ position : Position }> = ({ position }) =>
  <g fill="#a3dcf3" stroke="gray" strokeWidth="1">
    <circle cx={ position.x } cy={ position.y } r={ (SIZE.get('F1') ?? 1) } />
  </g>

const ObjectF2: React.FC<{ position : Position }> = ({ position }) =>
  <g fill="#a3dcf3" stroke="gray" strokeWidth="1">
    <circle cx={ position.x } cy={ position.y } r={ (SIZE.get('F2') ?? 1) } />
  </g>

const ObjectF3: React.FC<{ position : Position }> = ({ position }) =>
  <g fill="#a3dcf3" stroke="gray" strokeWidth="1">
    <circle cx={ position.x } cy={ position.y } r={ (SIZE.get('F3') ?? 1) } />
  </g>

const ObjectCYF: React.FC<{ position : Position }> = ({ position }) =>
  <g fill="#a3dcf3" stroke="gray" strokeWidth="1">
    <circle cx={ position.x } cy={ position.y } r={ (SIZE.get('CyF') ?? 1) } />
  </g>

const ObjectFCY: React.FC<{ position : Position }> = ({ position }) =>
  <g fill="#a3dcf3" stroke="gray" strokeWidth="1">
    <circle cx={ position.x } cy={ position.y } r={ (SIZE.get('FCy') ?? 1) } />
  </g>

const ObjectOV: React.FC<{ position : Position }> = ({ position }) =>
  <g fill="rgb(236,196,196)" fillOpacity="0.77" stroke="gray"
    transform={`translate(${position.x},${position.y}) rotate(${position.rotate})`}>
    <path d="M 0 0 L 4.994 27.034 L 27.945 8.191" strokeWidth="1.2" />
  </g>

const ObjectOther: React.FC<{ position : Position }> = ({ position }) =>
  <g fill="#00331b" stroke="gray" strokeWidth="1">
    <circle cx={ position.x } cy={ position.y } r={ (SIZE.get('OTHER') ?? 1) } />
  </g>




const OvarianViewer: React.FC<{
  structures : Structures,
  containerStyles?: React.CSSProperties
}> = ({ structures, containerStyles }) => {

  return (
    <div className={ styles.area } style={containerStyles}>
      <OneSideOvarian structures={ structures } side={ 'L' } />
      <OneSideOvarian structures={ structures } side={ 'R' } />
    </div>
  )
}

export default OvarianViewer
